import { Component, OnInit, OnDestroy, ElementRef, ViewChild, Renderer2, AfterViewChecked, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { WorkspaceService } from './workspace.service';
import { AppService } from '../app.service';
import { ALL_CUSTOM_ELEMENT_TAGS } from '../custom-element-tags.generated';

/**
 * CE tags that must NOT be auto-detected as full-screen CEs.
 * These are modal-only CEs whose naming convention happens to match
 * a workspace screen link (e.g. link "dividends" → "gtw-dividends-ce").
 */
const MODAL_ONLY_CE_TAGS = new Set<string>([
  'gtw-dividends-ce',
  'gtw-max-loan-balance-ce',
  'gtw-tax-liability-dashboard-ce',
  'gtw-form-1118-config-ce',
  'gtw-htko-config-ce',
  'gtw-recommended-automations-ce',
  'gtw-recommended-automations-confirm-ce',
  'gtw-retained-earnings-ce',
  'gtw-mef-efile-changes',
  'gtw-scenario-setup-ce',
  'gtw-amended-cycle-ce',
  'gtw-chart-rollover-maintenance-ce',
  'gtw-update-document-detail-ce',
  /** Grid tab is `gtw-grid`; this CE is row action / modal (updateEntityDetails). Link `entity-details` must not steal the tab body. */
  'gtw-entity-details-ce',
  /** Grid tab is `gtw-grid`; this CE is grid row-action / modal (postingPartners). Link `posting-partners` must not steal the tab body. */
  'gtw-posting-partners-ce',
  /** Grid tab is `gtw-grid`; this CE is grid row-action / modal (deltaPosting). Link `delta-posting` must not steal the tab body. */
  'gtw-delta-posting-ce',
  'gtw-tax-attributes-ce',
  // Confirm-only modal CEs whose tag name happens to collide with a
  // workspace screen link via the `gtw-<link>-ce` naming convention.
  // These must be opened via grid actions (ModalFactory / CeHostDialog),
  // never auto-mounted as the screen body.
  'gtw-settlement-redirection-ce',
  /** Workspace link `form-8082` matches the registered CE tag `gtw-form-8082`
   * exactly via the `gtw-<link>` candidate, so auto-detection would mount the
   * modal CE as the screen body. Form 8082 is modal-only — legacy
   * `ui/app/components/form8082/form8082.html` opens it via `$uibModalInstance`
   * with explicit `row-data` / `base-urls` / `global-params` bindings. Block
   * here so the grid (screen_key 11602) renders as the workspace tab instead. */
  'gtw-form-8082',
  /** Grid tab is `gtw-grid`; this CE is a modal opened from a grid row (creditDetails).
   * Link `credit-details` must not steal the tab body. */
  'gtw-credit-details-ce',
  /** Issue Management list screen CE; add/edit are modal-only. */
  'gtw-issue-add-ce',
  'gtw-issue-edit-ce',
]);

/**
 * Maps server route links (after stripping "workspace." prefix) to CE tag names
 * for screens where the link cannot be derived from the tag via naming convention.
 */
const SCREEN_CE_REGISTRY: Record<string, string> = {
  // Partnership foot-notes screen
  'foot-notes':                     'gtw-foot-notes-ce',
  // FTC settings route aliases
  'ftc-default':                    'gtw-component-defaults-rollover-ce',
  'defaults':                       'gtw-component-defaults-rollover-ce',
  'account-sync-to-ftc':            'gtw-act-sync-ftc-ce',
  'tag-sourcing-update':            'gtw-tag-sourcing-ce',
  'attribute-screen-demo':          'gtw-fir-edit-gtaps-attributes-ce',
  'attributeDemo':                  'gtw-fir-edit-gtaps-attributes-ce',
  'trans-category':                 'gtw-expense-trans-info-ce',
  'expenseTrans':                   'gtw-expense-trans-info-ce',
  'sync-process':                   'gtw-sync-process-ce',
  'auto-tag-process':               'gtw-auto-tag-process-ce',
  'auto-tags':                      'gtw-trans-jobs-ce',
  'form-1118-sync':                 'gtw-form-1118-sync-ce',
  'trans-catg-sync':                'gtw-trans-catg-sync-ce',
  'expense-a-a-calc-ftc-admin':     'gtw-expense-consol-config-ce',
  'component-defaults-rollover':    'gtw-component-defaults-rollover-ce',
  'component-defaults-rolloverr':   'gtw-component-defaults-rollover-ce',
  'assets-roll-over':               'gtw-asset-rollover-ce',
  'exams-roll-over':                'gtw-exams-rollover-ce',
  'branchtax-defaults':             'gtw-branchtax-summary-info-ce',
  'withholdtax-defaults':           'gtw-withhold-tax-summary-edit-ce',
  'exp-consol-conf':                'gtw-expense-consol-config-ce',
  'systemSettings':                 'gtw-me-sic-assignment-ce',
  'investment-validations':         'gtw-investment-validation-ce',
  '1118-schedule-j-data-entry':     'gtw-form-1118-schd-j-ce',
  'general-1118-schedule-k':        'gtw-form-1118-schd-k-ce',
  'passive-1118-schedule-k':        'gtw-form-1118-schd-k-ce',
  'branch-1118-schedule-k':         'gtw-form-1118-schd-k-ce',
  'gilti-1118-schedule-k':          'gtw-form-1118-schd-k-ce',
  'treaty-1118-schedule-k':         'gtw-form-1118-schd-k-ce',
  '901j-1118-schedule-k':           'gtw-form-1118-schd-k-ce',
  'ftc-k2-sync':                    'gtw-k2-sync-ce',
  'k3-overrides-allocation':        'gtw-k3-overrides-ce',
  'coa-diagnostic-fix':             'gtw-coa-diagnostic-fix-ce',
  // `workspace.diagnostics-maintanence` (server / navigation-json spelling) — custom
  // screen (DiagMaintCtrl → Entity Mgmt), not a grid; CE name does not follow gtw-<link>-ce.
  'diagnostics-maintanence':        'gtw-entity-mgmt-app-ce',
  'diagnostics-maintenance':        'gtw-entity-mgmt-app-ce',
  'issue-management':               'gtw-issue-management-list-ce',
  // QAR Standard Description management screen. Server link
  // `workspace.standard-description` does not match the `gtw-<link>-ce`
  // naming convention for `gtw-qar-std-desc-ce`, so map it explicitly.
  // Otherwise the workspace resolver falls back to <gtw-grid> (or /home).
  'standard-description':           'gtw-qar-std-desc-ce',
  'summary-diagnostics-new':        'gtw-summary-diagnostic-ce',
  'pship-ptr-details-k2':           'gtw-pship-ptr-ce',

  'ftc-k2k3_HEADER':                'gtw-f1065-k2-header-ce',
  'ftc-1065-part-i_P1_S0':          'gtw-f1065-k2-p1s0-ce',
  'ftc-section-4_P3_S4':            'gtw-f1065-k2-p3s4-ce',
  'ftc-section-1_P7_S1':            'gtw-f1065-k2-p7s1-ce',
  'ftc-part-7-section-2_P7_S2':     'gtw-f1065-k2-p7s2-ce',
  'ftc-k2-section-3_P10_S3':        'gtw-f1065-k2-p10s3-ce',
  'ftc-section-1065_P11_S0':        'gtw-f1065-k2-p11s0-ce',
  'part-xii':                        'gtw-f1065-k2-p12s0-ce',
  'ftc-section-1_P13_S0':           'gtw-f1065-k2-p13s0-ce',
  'k2-section-5_P3_S5':             'gtw-f1065-k2-data-entry-ce',
  'calcs-filing-group-data-setup':  'gtw-calcs-filing-group-status-ce',
  'calcs-scenario-data-setup':      'gtw-calcs-scenario-data-setup-ce',
  'irc_965_tax_return':             'gtw-section-965-tax-db-ce',
  'irc_965_tax_return_q1':          'gtw-section-965-tax-db-ce',
  'irc_965_tax_return_raf':         'gtw-section-965-tax-db-ce',
  // efile route aliases (legacy ui/app/components/efile/efile.js states)
  'efile-data-push':                'gtw-dst-efile-datapush-ce',
  'submissions-dasboard':           'gtw-efile-admin-shell-ce',
  'submissions-dashboard':          'gtw-efile-admin-shell-ce',
  'migration-hist':                 'gtw-efile-migration-history-ce',
  // PDF Forms — legacy `form-view.dashboard` / `state-form-view.dashboard`
  // uib-router states from `ui/app/components/formView/tabWrapper/formViewTab.js`.
  // These are top-level states (NOT children of `workspace`), so the live
  // URL is `/form-view/dashboard` and `/state-form-view/dashboard` — see
  // the matching routes in `app-routing.module.ts` which mount this
  // component with `data: { screenLink: 'form-view.dashboard' }` (etc.).
  // The state name contains a literal dot, so the `gtw-<link>-ce` naming
  // convention in `detectCustomElement` cannot synthesize a valid tag —
  // without an explicit registry entry the screen would fall through to
  // `<gtw-grid>`, which is wrong because form-view is a tree/PDF viewer.
  // Federal vs State is differentiated inside `FormViewComponent` via
  // `filterParams.screen_name.includes('state')` (`form-view.component.ts`
  // ~L268), so a single CE tag handles both.
  'form-view.dashboard':            'gtw-form-view-ce',
  'state-form-view.dashboard':      'gtw-form-view-ce',
  // EST: backend/navigation can send this legacy alias instead of `est-rollover`.
  // Without explicit mapping, workspace falls back to `<gtw-grid>` and the
  // custom-tab body never mounts.
  'est-quarter-rollover':           'gtw-est-rollover-ce',
  // EST quarter maintenance legacy alias.
  'est-quarter-maintenance':        'gtw-est-qtr-maintenance-ce',
  // Domestic - Limitation suite (single CE for multiple custom-tab links)
  'limitation-summary':                         'gtw-limitation-screen-ce',
  '1231-gain--loss-analysis':                  'gtw-limitation-screen-ce',
  'capital-loss-analysis':                     'gtw-limitation-screen-ce',
  'charitable-contributions-analysis':         'gtw-limitation-screen-ce',
  'dividends-received-deductions-worksheet':   'gtw-limitation-screen-ce',
  // Business Lock (link-scoped). Avoid key-based mapping here so unrelated
  // screens (e.g. filing-group-locking) are not forced into this CE.
  'business-lock': 'gtw-locking-ce',
  // Form 851 Part III (851 Textual Info) — server link `workspace.form-851-textual--info` does not
  // match the `gtw-<link>-ce` naming convention for `gtw-form851-ce`.
  'form-851-textual--info': 'gtw-form851-ce',
  '11217': 'gtw-form851-ce',
};

@Component({
    selector: 'gtw-workspace-screen',
    template: `
    @if (!isDashboard && !ceTagName) {
<gtw-grid [screenKey]="screenKey"></gtw-grid>
}
    @if (isDashboard) {
<gtw-ex-dashboard></gtw-ex-dashboard>
}
    @if (ceTagName) {
<div #ceHost class="ce-host"></div>
}
  `,
    styles: [`
    :host { display: block; height: 100%; }
    .ce-host { height: 100%; }
    .ce-host ::ng-deep .main-container { height: 100%; }
  `],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkspaceScreenComponent implements OnInit, OnDestroy, AfterViewChecked {
  screenKey: string = '';
  isDashboard = false;
  ceTagName: string = '';
  private routeSub!: Subscription;
  private cePending = false;

  @ViewChild('ceHost') ceHost!: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workspaceService: WorkspaceService,
    private appService: AppService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.params.subscribe((params) => {
      // Legacy uib-router states whose URL pattern can't fit the
      // `/workspace/:screenKey` resolver (e.g. `form-view.dashboard` →
      // `/form-view/dashboard`, registered top-level in
      // `ui/app/components/formView/tabWrapper/formViewTab.js`) are mounted
      // directly on this component with a `screenLink` in route data. Pick
      // it up before falling back to workspace activity inspection.
      let isCustomTab = false;
      const dataScreenLink: string =
        this.route.snapshot.data?.['screenLink'] || '';

      if (params['screenKey']) {
        const key = params['screenKey'];
        if (/^\d+$/.test(key)) {
          this.screenKey = key;
          this.ceTagName = '';
          this.isDashboard = this.checkDashboard();
          this.detectCustomElement();
        } else {
          const prefixedLink = 'workspace.' + key;
          const found = this.workspaceService.findScreenByLink(prefixedLink)
            || this.workspaceService.findScreenByLink(key);
          if (found) {
            this.screenKey = found.screen.screen_key;
            const currentKey = this.workspaceService.workspaceFactory.activeScreen?.screen_key;
            if (currentKey !== found.screen.screen_key) {
              this.workspaceService.setActivity(found.activity, true, found.screen);
            }
            const resolvedLink = found.screen?.link || prefixedLink;
            isCustomTab = this.appService.ROUTES?.[resolvedLink]?.type === 'custom-tab';
            this.isDashboard = this.checkDashboardByLink(resolvedLink);

            if (!this.isDashboard) {
              this.detectCustomElement(resolvedLink);
            }
          } else {
            // Screen not found by link — check if this is a known CE in the registry
            // before redirecting to home. This allows CE-only screens that aren't
            // configured as workspace activities.
            const registryTag = SCREEN_CE_REGISTRY[key];
            if (registryTag && ALL_CUSTOM_ELEMENT_TAGS.has(registryTag)) {
              this.screenKey = '';
              this.isDashboard = false;
              this.ceTagName = registryTag;
              this.cePending = true;
              // This CE has no USER_MENU entry so setActivity can't be called.
              // Emit a synthetic screen so the breadcrumb shows a human-readable
              // label rather than staying on the previous screen's path.
              const syntheticLabel = key
                .replace(/-/g, ' ')
                .replace(/\b\w/g, (c: string) => c.toUpperCase());
              this.workspaceService.setTabActive.next({ label: syntheticLabel });
            } else {
              // Unknown / stale alphanumeric slug — there is no screen the
              // user could meaningfully see at this URL. The empty grid shell
              // we'd otherwise render is a worse UX than just sending them
              // back to the home page (mirrors the wildcard route fallback
              // for non-`/workspace` paths).
              this.router.navigate(['/home']);
              return;
            }
          }
        }
      } else if (dataScreenLink) {
        // Top-level legacy URL (form-view, state-form-view, …): the route
        // bypasses the workspace shell, so we don't try to resolve a
        // workspace activity here — just run CE detection against the
        // legacy link. Federal vs State is differentiated inside
        // `FormViewComponent` via `filterParams.screen_name`
        // (`form-view.component.ts` ~L268).
        this.screenKey = '';
        this.isDashboard = false;
        this.detectCustomElement(dataScreenLink);
        // setActivity is never called on this path, so setTabActive is never
        // emitted and the breadcrumb stays on whichever screen was last active.
        // Find the screen in USER_MENU and activate it with _changeState=false
        // so only the breadcrumb/global-filters update — no router navigate loop.
        const foundByLink = this.workspaceService.findScreenByLink(dataScreenLink);
        if (foundByLink?.screen) {
          const currentKey = this.workspaceService.workspaceFactory.activeScreen?.screen_key;
          if (currentKey !== foundByLink.screen.screen_key) {
            this.workspaceService.setActivity(foundByLink.activity, false, foundByLink.screen);
          }
        }
      } else {
        this.screenKey =
          this.workspaceService.workspaceFactory.activeScreen?.screen_key || '';
        this.ceTagName = '';
        this.isDashboard = this.checkDashboard();
        this.detectCustomElement();
      }

      this.workspaceService.isCeScreen.next(!!this.ceTagName);
    });
  }

  ngAfterViewChecked(): void {
    if (this.cePending && this.ceHost) {
      this.cePending = false;
      this.renderCustomElement();
    }
  }

  private detectCustomElement(link?: string): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const screenLink = link || activeScreen?.link || '';
    const activeScreenKey = String(activeScreen?.screen_key || this.screenKey || '');

    const rawLink = screenLink.startsWith('workspace.')
      ? screenLink.substring('workspace.'.length)
      : screenLink;

    if (!rawLink) {
      this.ceTagName = '';
      return;
    }

    const registryTag = SCREEN_CE_REGISTRY[rawLink];
    if (registryTag && ALL_CUSTOM_ELEMENT_TAGS.has(registryTag)) {
      this.ceTagName = registryTag;
      this.cePending = true;
      return;
    }

    const registryTagByKey = SCREEN_CE_REGISTRY[activeScreenKey];
    if (registryTagByKey && ALL_CUSTOM_ELEMENT_TAGS.has(registryTagByKey)) {
      this.ceTagName = registryTagByKey;
      this.cePending = true;
      return;
    }

    const candidates = [
      'gtw-' + rawLink,
      rawLink,
      rawLink + '-ce',
      'gtw-' + rawLink + '-ce',
    ];

    for (const tag of candidates) {
      if (ALL_CUSTOM_ELEMENT_TAGS.has(tag) && !MODAL_ONLY_CE_TAGS.has(tag)) {
        this.ceTagName = tag;
        this.cePending = true;
        return;
      }
    }

    this.ceTagName = '';
  }

  private renderCustomElement(): void {
    const host = this.ceHost?.nativeElement;
    if (!host || !this.ceTagName) return;

    host.innerHTML = '';
    const el = document.createElement(this.ceTagName);
    el.style.display = 'block';
    el.style.height = '100%';
    this.renderer.appendChild(host, el);
  }

  private checkDashboard(): boolean {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen?.link) return false;
    return this.checkDashboardByLink(activeScreen.link);
  }

  private checkDashboardByLink(link: string): boolean {
    const routeConfig = this.appService.ROUTES?.[link];
    // custom-tab screens are NOT dashboards, even if their legacy templateUrl contains 'dashboardExecutive'
    if (routeConfig?.type === 'custom-tab') {
      return false;
    }
    return !!(routeConfig?.type === 'dashboard' || routeConfig?.component === 'ExDashboardComponent' || routeConfig?.templateUrl?.includes('dashboardExecutive'));
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }
}
